import React, {useEffect} from 'react';
import './IngestConfig.scss';
import {useForm, useField} from 'react-final-form-hooks';
import wrap from 'wraptile';
import {curry, omit} from 'ramda';
import {adminRoutes} from 'App/AppRouter/routes';
import {useHistory, useParams} from 'react-router-dom';
import {formatDateToServer} from '@pluto/react-select-mapping';
import {maybeArray} from '@pluto/maybe-array';
import {func, object} from 'prop-types';

import IngestConfig from './IngestConfig';

const onSubmit = () => {};

export const getIsPublishedDateShown = (isMrss: any, isStatusActive: any) =>
    isMrss && isStatusActive;

export const getIngestDefaultsValues = (values: any) => {
    const {
        rating,
        category,
        language,
        copyrightHolder,
        copyrightYear,
        territories,
        rightTypes,
        captionsRequired,
    } = values;

    return {
        rating,
        category,
        language,
        copyrightHolder,
        copyrightYear,
        territories,
        rightTypes,
        captionsRequired,
    };
};

export const notAllowedKeys = [
    'id',
    'camundaProcessId',
    'createdAt',
    'updatedAt',
    'deletedAt',
    'nextStartDate',
    'lastStartDate',
    'rating',
    'category',
    'language',
    'copyrightHolder',
    'copyrightYear',
    'territories',
    'rightTypes',
    'captionsRequired',
    'isValidDate',
    'feedsLimit',
];

export const defaultRightTypesValue = ['Linear', 'AVOD'];

export const getValueOrFalse = (value: any) => value || false;
export const getFeedsLimitToServer = (value: any) => (value === 'ALL' ? 0 : value);
export const getFeedsLimitInitialValue = (value: any) => (value === 0 ? 'ALL' : value);
export const isValidFieldValidation = (value: boolean) => {
    if (!value) return 'There is not valid date in form';
};
export const getRightTypes = (value: any) => value || defaultRightTypesValue;

export const getFilteredValues = (defaultValues: any) => {
    if (!defaultValues) return {};

    return omit(notAllowedKeys, defaultValues);
};

export const getValidation = (values: any) => {
    const {
        name,
        authenticationRequired,
        username,
        password,
        contentPartnerId,
        ingestType,
        timeout,
        timeoutType,
        url,
        language,
        rightTypes,
        territories,
        labPartner,
    } = values;

    if (name && name.length > 50) {
        return 'Exceeded max limit';
    }

    if (!name || !contentPartnerId || !url || !ingestType) {
        return 'Field is required';
    }

    if (authenticationRequired && (!username || !password)) {
        return 'Field is required';
    }

    if (
        ingestType === 'MRSS' &&
        (
            !language ||
            !rightTypes ||
            !rightTypes.length ||
            !territories ||
            !timeout ||
            !timeoutType
        )
    ) {
        return 'Field is required';
    }

    if (ingestType === 'API' && (!labPartner || !timeout || !timeoutType)) {
        return 'Field is required';
    }

    if (ingestType === 'DROP_FOLDER' && !labPartner) {
        return 'Field is required'
    }

    return '';
};

interface IngestConfigContainerProps {
    saveIngestConfigItem: Function;
    setInitialValuesForEditingItem: Function;
    resetInitialValuesForEditingItem: Function;
    initialValues: any;
}

export default function IngestConfigContainer(props: IngestConfigContainerProps) {
    const timer = {};
    const {
        saveIngestConfigItem,
        setInitialValuesForEditingItem,
        resetInitialValuesForEditingItem,
        initialValues: defaultValues,
    } = props;
    const {
        feedDefaults,
        authenticationRequired,
        deliveryRequired,
        qcRequired,
        feedsLimit,
    } = defaultValues;
    const initialValues = {
        ...getFilteredValues(defaultValues),
        copyrightHolder: feedDefaults?.copyrightHolder || '',
        category: feedDefaults?.category || '',
        language: feedDefaults?.language || '',
        copyrightYear: feedDefaults?.copyrightYear || '',
        rating: feedDefaults?.rating || '',
        territories: feedDefaults?.territories || '',
        feedsLimit: getFeedsLimitInitialValue(feedsLimit),
        captionsRequired: getValueOrFalse(feedDefaults?.captionsRequired),
        authenticationRequired: getValueOrFalse(authenticationRequired),
        qcRequired: getValueOrFalse(qcRequired),
        deliveryRequired: getValueOrFalse(deliveryRequired),
        rightTypes: getRightTypes(feedDefaults?.rightTypes),
        isValidDate: true,
    };
    const {form, valid} = useForm({
        onSubmit,
        // @ts-expect-error ts-migrate(2322) FIXME: Type '""' is not assignable to type 'ValidationErr... Remove this comment to see the full error message
        validate: getValidation,
        initialValues,
    });
    const history = useHistory();
    const {id} = useParams<{id: string}>();
    const formValues = form.getState().values;
    const onPushRouteToHistory = pushRouteToHistory(history);

    const onSaveHandler = saveHandler({
        timer,
        saveIngestConfigItem,
        filteredValues: getFilteredValues(formValues),
        feedsLimit: formValues.feedsLimit,
        feedDefaultsValues: getIngestDefaultsValues(formValues),
        onPushRouteToHistory,
        id,
    });

    const setInitialValues = onEffect({
        id,
        setInitialValuesForEditingItem,
        resetInitialValuesForEditingItem,
        timer,
    });

    useEffect(setInitialValues, []);

    const isMrssAdditionalFieldsShown = form.getState().values.ingestType === 'MRSS';
    const isApiAdditionalFieldsShown = form.getState().values.ingestType === 'API';
    const isDropFolderAdditionalFieldsShown = form.getState().values.ingestType === 'DROP_FOLDER';
    const isStatusActive = form.getState().values.status === 'ACTIVE';
    const isPublishedDateShown = isStatusActive && isMrssAdditionalFieldsShown;
    const pollingIntervalType = form.getState().values.timeoutType || '';

    const feedNameField = useField('name', form);
    const contentPartnerField = useField('contentPartnerId', form);
    const labPartnerField = useField('labPartner', form);
    const feedTypeField = useField('provider', form);
    const ingestTypeField = useField('ingestType', form);
    const pollingIntervalTypeField = useField('timeoutType', form);
    const pollingIntervalValueField = useField('timeout', form);
    const ratingField = useField('rating', form);
    const categoryField = useField('category', form);
    const languageField = useField('language', form);
    const urlField = useField('url', form);
    const authenticationRequiredField = useField('authenticationRequired', form);
    const userNameField = useField('username', form);
    const teritoriesField = useField('territories', form);
    const passwordField = useField('password', form);
    const statusField = useField('status', form);
    const publishedDateField = useField('minPublishDate', form);
    const rightTypesField = useField('rightTypes', form);
    const captionsRequiredField = useField('captionsRequired', form);
    const copyrightHolderField = useField('copyrightHolder', form);
    const copyrightYearField = useField('copyrightYear', form);
    const feedsLimitField = useField('feedsLimit', form);
    const qcRequiredField = useField('qcRequired', form);
    const deliveryRequiredField = useField('deliveryRequired', form);
    const isValidDate = useField('isValidDate', form, isValidFieldValidation);

    const onPollingIntervalTypeChange = handlePollingIntervalTypeFieldChange(
        pollingIntervalValueField.input.onChange,
        pollingIntervalTypeField.input.onChange
    );

    return (
        <IngestConfig
            isApiAdditionalFieldsShown={isApiAdditionalFieldsShown}
            isDropFolderAdditionalFieldsShown={isDropFolderAdditionalFieldsShown}
            isValidDate={isValidDate}
            labPartnerField={labPartnerField}
            ingestTypeField={ingestTypeField}
            qcRequiredField={qcRequiredField}
            deliveryRequiredField={deliveryRequiredField}
            feedNameField={feedNameField}
            rightTypesField={rightTypesField}
            publishedDateField={publishedDateField}
            contentPartnerField={contentPartnerField}
            isPublishedDateShown={isPublishedDateShown}
            feedTypeField={feedTypeField}
            isMrssAdditionalFieldsShown={isMrssAdditionalFieldsShown}
            ratingField={ratingField}
            categoryField={categoryField}
            languageField={languageField}
            teritoriesField={teritoriesField}
            pollingIntervalTypeField={pollingIntervalTypeField}
            pollingIntervalValueField={pollingIntervalValueField}
            captionsRequiredField={captionsRequiredField}
            pollingIntervalType={pollingIntervalType}
            copyrightHolderField={copyrightHolderField}
            copyrightYearField={copyrightYearField}
            urlField={urlField}
            authenticationRequiredField={authenticationRequiredField}
            passwordField={passwordField}
            userNameField={userNameField}
            statusField={statusField}
            feedsLimitField={feedsLimitField}
            onSaveHandler={onSaveHandler}
            onPushRouteToHistory={onPushRouteToHistory}
            valid={valid}
            onPollingIntervalTypeChange={onPollingIntervalTypeChange}
        />
    );
}

IngestConfigContainer.propTypes = {
    saveIngestConfigItem: func.isRequired,
    setInitialValuesForEditingItem: func,
    initialValues: object,
};

IngestConfigContainer.defaultProps = {
    setInitialValuesForEditingItem: () => {},
    initialValues: {},
};

export const pushRouteToHistory = wrap((history: any) => {
    history.push(adminRoutes.ingestConfigs);
});

export const getTimeout = (timeout: any, timeoutType: any) => {
    if (timeoutType === 'TEST') return [formatDateToServer(timeout)];

    return maybeArray(timeout);
};

export const saveHandler = wrap(
    ({
        timer,
        saveIngestConfigItem,
        filteredValues,
        feedsLimit,
        feedDefaultsValues,
        onPushRouteToHistory,
        id,
    }: any) => {
        const {
            minPublishDate,
            timeout,
            timeoutType,
            ingestType,
            status,
            authenticationRequired,
            qcRequired,
        } = filteredValues;

        let formValues = {
            ...filteredValues,
            feedDefaults: null,
            minPublishDate: null,
        };

        if (timeoutType === 'TEST') {
            formValues = {
                ...formValues,
                feedsLimit: getFeedsLimitToServer(feedsLimit),
            };
        }

        if (ingestType === 'MRSS') {
            formValues = {
                ...formValues,
                feedDefaults: feedDefaultsValues,
                timeout: getTimeout(timeout, timeoutType),
            };
        }

        if (ingestType === 'API') {
            formValues = {
                ...formValues,
                timeout: getTimeout(timeout, timeoutType),
            }
        }

        if (status === 'ACTIVE') {
            formValues = {
                ...formValues,
                minPublishDate: formatDateToServer(minPublishDate),
            };
        }

        if (!authenticationRequired) {
            formValues = {
                ...formValues,
                username: null,
                password: null,
            };
        }

        if (qcRequired) {
            formValues = omit(['deliveryRequired'], formValues);
        }

        saveIngestConfigItem({
            id,
            body: formValues,
        });
        timer.value = setTimeout(onPushRouteToHistory, 1000);
    }
);

export const handlePollingIntervalTypeFieldChange = curry(
    (onValueFieldChange, onTypeFieldChange, value) => {
        onValueFieldChange(null);
        onTypeFieldChange(value);
    }
);

export const onEffectCleanup = wrap((handlers: any) => {
    const {wrapperClearTimeout, resetInitialValuesForEditingItem} = handlers;

    wrapperClearTimeout();

    if (resetInitialValuesForEditingItem) resetInitialValuesForEditingItem();
});

export const onEffect = wrap((handlers: any) => {
    const {
        id,
        setInitialValuesForEditingItem,
        resetInitialValuesForEditingItem,
        timer,
    } = handlers;

    if (id) setInitialValuesForEditingItem(id);

    const wrapperClearTimeout = wrap(clearTimeout);
    const cleanup = onEffectCleanup({
        wrapperClearTimeout: wrapperClearTimeout(timer.value),
        resetInitialValuesForEditingItem,
    });

    return cleanup;
});
